import React, { useState } from 'react';
import {
  PaymentLinkError,
  PaymentLinkSendResult,
  renewPaymentLink,
  resendPaymentLink,
} from '../../services/paymentLinkService';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/formatters';
import './PaymentLinkSection.css';

interface PaymentLinkSectionProps {
  confirmationCode: string;
  /** Prefilled recipient; managers can override before sending. */
  guestEmail?: string;
}

type Notice =
  | { tone: 'success'; text: string }
  | { tone: 'error'; text: string; offerRenew?: boolean };

/**
 * Resend / renew the payment-link email for a reservation. The email URL is
 * generated server-side (hcp-backend PAYMENT_LINK_BASE_URL), so after that
 * env var is corrected, resending from here delivers a working link to
 * guests who received a broken one.
 */
const PaymentLinkSection: React.FC<PaymentLinkSectionProps> = ({ confirmationCode, guestEmail }) => {
  const [email, setEmail] = useState(guestEmail ?? '');
  const [busy, setBusy] = useState<'resend' | 'renew' | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  // Always send the box's value as the explicit recipient. The prefill is the
  // member's email, but the backend's default (no override) is the *customer*
  // email — so suppressing the override when it matches the prefill would
  // silently route to a different address than the one shown here.
  const overrideEmail = email.trim() || undefined;

  const report = (result: PaymentLinkSendResult) => {
    setNotice({
      tone: 'success',
      text: `Payment link for ${formatCurrency(result.amount)} sent to ${result.sent_to} — expires ${formatDate(result.expires_at)}.`,
    });
  };

  const handleResend = async () => {
    setBusy('resend');
    setNotice(null);
    try {
      report(await resendPaymentLink(confirmationCode, overrideEmail));
    } catch (err) {
      const linkError = err as PaymentLinkError;
      setNotice({ tone: 'error', text: linkError.message, offerRenew: linkError.kind === 'expired' });
    } finally {
      setBusy(null);
    }
  };

  const handleRenew = async () => {
    if (!window.confirm('Issue a brand-new payment link and email it to the guest? The old link stops working.')) {
      return;
    }
    setBusy('renew');
    setNotice(null);
    try {
      report(await renewPaymentLink(confirmationCode, overrideEmail));
    } catch (err) {
      setNotice({ tone: 'error', text: (err as PaymentLinkError).message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="popup-section payment-link-section">
      <h3 className="section-title">Payment Link</h3>
      <p className="payment-link-hint">
        Email the guest their secure payment link again. Use <strong>Renew</strong> if the
        link has expired — it issues a fresh one.
      </p>
      <div className="payment-link-controls">
        <input
          type="email"
          className="payment-link-email"
          placeholder="Recipient email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={busy !== null}
        />
        <button
          className="btn btn-blue btn-sm"
          onClick={handleResend}
          disabled={busy !== null || !email.trim()}
        >
          {busy === 'resend' ? 'Sending…' : 'Resend Link'}
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={handleRenew}
          disabled={busy !== null || !email.trim()}
        >
          {busy === 'renew' ? 'Renewing…' : 'Renew Expired Link'}
        </button>
      </div>
      {notice && (
        <p className={`payment-link-notice payment-link-notice--${notice.tone}`}>
          {notice.tone === 'error' ? '⚠️ ' : '✅ '}
          {notice.text}
          {notice.tone === 'error' && notice.offerRenew && (
            <>
              {' '}
              <button className="payment-link-inline-renew" onClick={handleRenew} disabled={busy !== null}>
                Renew now
              </button>
            </>
          )}
        </p>
      )}
    </section>
  );
};

export default PaymentLinkSection;
